import { useSyncExternalStore } from 'react';

/**
 * Which playlists this browser shows in reverse — the display-order option.
 *
 * **Display-only by force**: the API has no reorder endpoint (§7i), so the entries' stored order
 * cannot change and the only honest "reverse" is one applied at render time. That is also why it is
 * saved *in the browser* rather than anywhere synced — there is nothing on the instance for it to be.
 *
 * `localStorage`, unlike `privacyMemory.ts`'s in-memory map, because the two remember different kinds
 * of fact: that store caches our own *writes* (a stale entry is a confident lie), this one holds a
 * *preference* — stale is impossible, it means whatever the reader last chose. Capped: an id is ~36
 * bytes and only flipped playlists are stored, so the cap is a backstop, not a policy.
 */

const KEY = 'visious.playlistOrder';
const MAX_ENTRIES = 500;

function read(): readonly string[] {
  try {
    const raw = JSON.parse(globalThis.localStorage.getItem(KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    // Blocked storage or a half-written value — every playlist reads forward, which is the default.
    return [];
  }
}

let snapshot: ReadonlySet<string> | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): ReadonlySet<string> {
  snapshot ??= new Set(read());
  return snapshot;
}

function publish(): void {
  snapshot = null;
  for (const listener of listeners) listener();
}

/**
 * One `storage` listener for the whole document, installed on the first subscriber and never taken
 * down. It used to be one per *subscriber*, which made a single cross-tab write cost one full re-read
 * and one round of notifications **per subscriber** — see `platform/settings.ts`, where a grid made
 * that quadratic and froze the tab for seconds.
 */
let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs) return;
  watchingOtherTabs = true;
  // Another tab flipping the order should reach an open page — the settings store's reason.
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === null || event.key === KEY) publish();
  });
}

function subscribe(listener: () => void): () => void {
  watchOtherTabs();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setPlaylistReversed(playlistId: string, reversed: boolean): void {
  const current = read().filter((id) => id !== playlistId);
  const next = reversed ? [...current.slice(-(MAX_ENTRIES - 1)), playlistId] : current;
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // A choice that cannot be saved still applies to this page load, via the snapshot below.
    snapshot = new Set(next);
    for (const listener of listeners) listener();
    return;
  }
  publish();
}

/** The Data tab's "clear local settings" — every playlist reads forward again, live pages included. */
export function clearPlaylistOrderMemory(): void {
  try {
    globalThis.localStorage.removeItem(KEY);
  } catch {
    // Blocked storage holds nothing to clear; the in-memory snapshot still resets below.
  }
  publish();
}

export function usePlaylistReversed(playlistId: string): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot).has(playlistId);
}

// Stable, not rebuilt per call — a changing server snapshot re-renders forever.
const EMPTY_SET: ReadonlySet<string> = new Set<string>();
const getServerSnapshot = (): ReadonlySet<string> => EMPTY_SET;
